import React, { useState, useEffect, useMemo } from 'react'
import { Channel } from 'stream-chat'
import { useChatContext } from 'stream-chat-react'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { useGetAdvisorDashboardQuery } from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from 'components/Chat/helper'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { Plus, Search, Trash2, Undo2 } from 'lucide-react'
import type { StreamChatGenerics } from '../../types'

interface GroupEditProps {
  channel: Channel<StreamChatGenerics>
  onClose: () => void
}

const GroupEdit: React.FC<GroupEditProps> = ({ channel, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [users, setUsers] = useState<IAssigneeOptions[]>([])
  const [isUpdating, setIsUpdating] = useState(false)
  const [membersToAdd, setMembersToAdd] = useState<Set<string>>(new Set())
  const [membersToRemove, setMembersToRemove] = useState<Set<string>>(new Set())

  const { client } = useChatContext()
  useUserInfo()
  const tenantId = useTenantId()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)

  // Get business profile to access team members
  const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

  // Memoize assignee options to prevent infinite re-renders
  const assigneeOptions = useMemo(() => {
    let options: IAssigneeOptions[] = []

    if (!isAdvisor) {
      options = businessAssigneeOptions
    } else {
      const response = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
      options =
        response?.accounts?.map((account: any) => ({
          label: `${account?.firstName} ${account?.lastName}`,
          value: account?.id,
          email: account?.email,
          user: {
            id: account?.id,
            firstName: account?.firstName,
            lastName: account?.lastName,
            email: account?.email,
          },
        })) || []
    }

    // Filter out current user
    return options.filter((option) => option.value !== loggedInUser?.id)
  }, [isAdvisor, businessAssigneeOptions, advisorData, loggedInUser?.id])

  useEffect(() => {
    setUsers(assigneeOptions)
  }, [assigneeOptions])

  // Get current channel members (excluding current user) with pending changes
  const currentMembers = useMemo(() => {
    const memberIds = Object.keys(channel.state.members || {}).filter((id) => id !== client.userID)
    // Filter out members marked for removal, add members marked for addition
    const effectiveMemberIds = memberIds
      .filter((id) => !membersToRemove.has(id))
      .concat(Array.from(membersToAdd))
    return users.filter((user) => effectiveMemberIds.includes(user.value))
  }, [channel.state.members, users, client.userID, membersToAdd, membersToRemove])

  // Get unselected users filtered by search (including pending changes)
  const availableUsers = useMemo(() => {
    const memberIds = Object.keys(channel.state.members || {})
    // Filter out members marked for removal, add members marked for addition
    const effectiveMemberIds = new Set([
      ...memberIds.filter((id) => !membersToRemove.has(id)),
      ...Array.from(membersToAdd),
    ])

    return users.filter(
      (user) =>
        !effectiveMemberIds.has(user.value) &&
        user.label.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [users, channel.state.members, searchQuery, membersToAdd, membersToRemove])

  const handleAddMember = (userId: string) => {
    // If this member was marked for removal, unmark them
    if (membersToRemove.has(userId)) {
      const newSet = new Set(membersToRemove)
      newSet.delete(userId)
      setMembersToRemove(newSet)
    } else {
      // Mark for addition
      const newSet = new Set(membersToAdd)
      newSet.add(userId)
      setMembersToAdd(newSet)
    }
  }

  const handleRemoveMember = (userId: string) => {
    // Don't allow removing the current user
    if (userId === client.userID) {
      return
    }

    // If this member was marked for addition, unmark them
    if (membersToAdd.has(userId)) {
      const newSet = new Set(membersToAdd)
      newSet.delete(userId)
      setMembersToAdd(newSet)
    } else {
      // Mark for removal
      const newSet = new Set(membersToRemove)
      newSet.add(userId)
      setMembersToRemove(newSet)
    }
  }

  const handleSaveChanges = async () => {
    // Check if there are any changes
    if (membersToAdd.size === 0 && membersToRemove.size === 0) {
      onClose()
      return
    }

    setIsUpdating(true)
    try {
      // Add members first
      if (membersToAdd.size > 0) {
        await channel.addMembers(Array.from(membersToAdd))
      }

      // Then remove members
      if (membersToRemove.size > 0) {
        await channel.removeMembers(Array.from(membersToRemove))
      }

      onClose()
    } catch (error) {
      console.error('Failed to update members:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const channelName = channel.data?.name || 'Group Chat'
  // Calculate total members including pending changes
  const originalMemberCount = Object.keys(channel.state.members || {}).length
  const totalMembers = originalMemberCount + membersToAdd.size - membersToRemove.size
  const hasChanges = membersToAdd.size > 0 || membersToRemove.size > 0

  return (
    <AppModalBox
      onClose={onClose}
      classes="p-0"
      height="h-auto"
      width="w-[540px] max-w-md"
      wrapperClass="flex items-center justify-center"
      title={channelName}
      titleFont="font-semibold"
      subtitle={`${totalMembers} members`}
    >
      <div className="bg-app-white-100 rounded-2xl shadow-xl w-full max-h-[90vh] flex flex-col">
        {/* Current Members Section */}
        {currentMembers.length > 0 && (
          <div className="px-6 py-4 border-b border-app-grey-25">
            <h3 className="text-sm font-medium text-app-grey-70 mb-4">Members</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {/* Show current user first */}
              <div className="flex items-center gap-3 p-2">
                <AppAvatar
                  size="md"
                  type="mixed"
                  shape="Rounded"
                  text={getInitials({
                    firstName: loggedInUser?.firstName || '',
                    lastName: loggedInUser?.lastName || '',
                  })}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-black-100 truncate">
                    {loggedInUser?.firstName} {loggedInUser?.lastName} (You)
                  </p>
                  {loggedInUser?.email && (
                    <p className="text-xs text-app-grey-55 truncate">{loggedInUser.email}</p>
                  )}
                </div>
              </div>
              {/* Show other members */}
              {currentMembers.map((member) => {
                const isMarkedForRemoval = membersToRemove.has(member.value)
                const isNewlyAdded = membersToAdd.has(member.value)
                return (
                  <div
                    key={member.value}
                    className={`flex items-center gap-3 p-2 ${
                      isMarkedForRemoval ? 'opacity-50' : ''
                    } ${isNewlyAdded ? 'bg-app-green-20 rounded-lg' : ''}`}
                  >
                    <AppAvatar
                      size="md"
                      type="mixed"
                      shape="Rounded"
                      text={getInitials({
                        firstName: member.user?.firstName || '',
                        lastName: member.user?.lastName || '',
                      })}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium text-app-black-100 truncate ${
                          isMarkedForRemoval ? 'line-through' : ''
                        }`}
                      >
                        {member.label}
                        {isNewlyAdded && <span className="text-app-green-60 ml-2">(New)</span>}
                      </p>
                      {member.email && (
                        <p className="text-xs text-app-grey-55 truncate">{member.email}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveMember(member.value)}
                      className={`p-1.5 hover:bg-app-grey-10 rounded-lg transition-colors duration-150 ${
                        isMarkedForRemoval ? 'bg-app-red-20' : ''
                      }`}
                      aria-label={isMarkedForRemoval ? 'Undo remove' : 'Remove member'}
                      disabled={isUpdating}
                    >
                      {isMarkedForRemoval ? (
                        <Undo2 className="w-4 h-4 text-app-grey-55" />
                      ) : (
                        <Trash2 className="w-4 h-4 text-app-grey-55" />
                      )}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Add Members Section */}
        <div className="px-6 py-4 border-b border-app-grey-25">
          <h3 className="text-sm font-medium text-app-grey-70 mb-4">Add members</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-app-grey-55" />
            <input
              type="text"
              placeholder="Search name...."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isUpdating}
              className="w-full pl-10 pr-4 py-2.5 bg-app-grey-20 border border-app-grey-25 rounded-lg text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent disabled:opacity-50"
            />
          </div>
        </div>

        {/* Available Users List */}
        <div className="flex-1 overflow-y-auto max-h-64">
          <div className="px-6">
            {availableUsers.length > 0 ? (
              availableUsers.map((user) => (
                <div
                  key={user.value}
                  className={`flex items-center gap-3 py-3 cursor-pointer hover:bg-app-grey-10 rounded-lg px-2 ${
                    membersToAdd.has(user.value) ? 'bg-app-green-20' : ''
                  }`}
                  onClick={() => !isUpdating && handleAddMember(user.value)}
                >
                  <AppAvatar
                    size="md"
                    type="mixed"
                    shape="Rounded"
                    text={getInitials({
                      firstName: user.user?.firstName || '',
                      lastName: user.user?.lastName || '',
                    })}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-black-100 truncate">{user.label}</p>
                    {user.email && (
                      <p className="text-xs text-app-grey-55 truncate">{user.email}</p>
                    )}
                  </div>
                  <div className="w-5 h-5 rounded border-2 border-app-grey-30 bg-white flex items-center justify-center">
                    <Plus className="w-3 h-3 text-app-grey-55" />
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-app-grey-55">
                  {searchQuery ? 'No users found' : 'All users have been added'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-app-grey-25">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isUpdating}
              className="flex-1 py-2.5 px-4 rounded-lg font-medium transition-colors duration-150 border border-app-grey-30 text-app-grey-70 hover:bg-app-grey-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={isUpdating || !hasChanges}
              className="flex-1 py-2.5 px-4 rounded-lg font-medium transition-colors duration-150 bg-app-primary-60 text-app-white-100 hover:bg-app-primary-70 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? 'Saving...' : 'Done'}
            </button>
          </div>
          {hasChanges && !isUpdating && (
            <p className="text-xs text-app-grey-55 text-center mt-2">
              {membersToAdd.size > 0 && membersToRemove.size > 0
                ? `Adding ${membersToAdd.size} and removing ${membersToRemove.size} member${
                    membersToRemove.size > 1 ? 's' : ''
                  }`
                : membersToAdd.size > 0
                ? `Adding ${membersToAdd.size} member${membersToAdd.size > 1 ? 's' : ''}`
                : `Removing ${membersToRemove.size} member${membersToRemove.size > 1 ? 's' : ''}`}
            </p>
          )}
        </div>
      </div>
    </AppModalBox>
  )
}

export default GroupEdit
